import classNames from "classnames";
import { ReactNode } from "react";
import { Delta } from "../../types";
import DeltaBadge from "../DeltaBadge";
import styles from "./style.module.scss";

interface MetricTileProps {
  label: ReactNode;
  /** Glyph shown in the tinted square at the top right. */
  icon?: ReactNode;
  value?: ReactNode;
  /** Sits on the value's baseline, e.g. "trials" or "53.2% of 2,270 enrolled". */
  unit?: ReactNode;
  delta?: Delta;
  /** Definition line pinned to the bottom behind a dashed rule. */
  definition?: ReactNode;
  /** Rings and anything else between the value and the delta. */
  children?: ReactNode;
  /** Rendered after the delta, where the design puts its school chips. */
  footer?: ReactNode;
  /** Larger type used by the headline tiles. */
  emphasis?: boolean;
  className?: string;
}

/**
 * The tinted tile used across Account information and Headline figures.
 * Only the parts it is given are rendered, so one component covers every
 * variation in the design without prop gymnastics.
 */
function MetricTile({
  label,
  icon,
  value,
  unit,
  delta,
  definition,
  children,
  footer,
  emphasis,
  className,
}: MetricTileProps) {
  return (
    <div className={classNames(styles.tile, emphasis && styles.emphasis, className)}>
      <div className={styles.head}>
        <div className={styles.label}>{label}</div>
        {icon && <span className={styles.icon}>{icon}</span>}
      </div>

      {(value !== undefined || unit) && (
        <div className={styles.figure}>
          {value !== undefined && <div className={styles.value}>{value}</div>}
          {unit && <div className={styles.unit}>{unit}</div>}
        </div>
      )}

      {children}

      {delta && (
        <DeltaBadge
          delta={delta}
          size={emphasis ? "lg" : "md"}
          className={styles.delta}
        />
      )}

      {footer}

      {definition && <div className={styles.definition}>{definition}</div>}
    </div>
  );
}

export default MetricTile;
